import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import axios from 'axios'
import PropTypes from 'prop-types'
import { createContext, useContext, useMemo } from 'react'

interface ConfigContext {
  proxyUrl?: string
  // getHeaders: () => Record<string, string>
}

const configContext = createContext<ConfigContext | null>(null)

export function ConfigProvider({
  config,
  children,
}: {
  config: ConfigContext
  children: React.ReactNode
}) {
  const proxyUrl = (() => {
    if (config.proxyUrl) {
      const baseUrl = new URL(config.proxyUrl)
      return `${baseUrl?.hostname}${baseUrl.pathname}` || ''
    } else if (process.env.REACT_APP_GRAPHQL_URL) {
      const baseUrl = new URL(process.env.REACT_APP_GRAPHQL_URL)
      return `${baseUrl?.hostname}${baseUrl.pathname}` || ''
    } else {
      return ''
    }
  })()

  function isExpired(token: string) {
    const jwtBase64url = token.split('.')?.[1];
    const jwtBase64 = jwtBase64url
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const jsonBlob = decodeURIComponent(
      atob(jwtBase64)
        .split('')
        .map(
          (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        )
        .join('')
    );
    const jwtObject = JSON.parse(jsonBlob);
    const expiryTime = jwtObject.exp
    if (Date.now() >= expiryTime * 1000) {
      return true;
    } else {
      return false
    }
  }
  
  const getHeaders = async () => {
    async function token () {
      const localToken = localStorage.getItem('core_auth_token')
      if (!localToken || isExpired(localToken)) {
        const client = process.env.REACT_APP_CLIENT_DETAILS
        const url = process.env.REACT_APP_COGNITO_DOMAIN || ''
        const params = new URLSearchParams()
        params.append('grant_type', 'client_credentials')
        params.append('scope', 'graphql/api')
        const config = {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Basic ${client}`
          }
        }
      
        const response = await axios.post(url, params, config)
        const token = response.data?.access_token
        if (!token) {
          throw new Error('No access_token from auth server')
        }
        localStorage.setItem('core_auth_token', token)
        return token
      }
      return localToken
    }
  
    const tokenToUse = await token()
    return {
      Authorization: `Bearer ${tokenToUse}`,
    };
  }

  const client = useMemo(() => {
    const httpLink = createHttpLink({
      uri: `https://${proxyUrl}`,
      // options: { mode: 'no-cors', fetchPolicy: 'no-cache' },
    })

    const wsLink = new WebSocketLink({
      uri: `wss://${proxyUrl}`,
      options: {
        reconnect: true,
        connectionParams: async () => {
          const headers = await getHeaders()
          return headers
        },
      },
    })

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      },
      wsLink,
      httpLink
    )

    const authLink = setContext(async (_, { headers }) => {
      const additionalHeaders = await getHeaders() || {}

      return {
        headers: {
          ...headers,
          ...additionalHeaders,
        },
      }
    })

    const errorLink = onError(() => {})

    const link = ApolloLink.from([authLink, errorLink, splitLink])

    return new ApolloClient({
      cache: new InMemoryCache(),
      link,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyUrl])

  return (
    <configContext.Provider
      value={{
        ...config,
      }}
    >
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </configContext.Provider>
  )
}

ConfigProvider.propTypes = {
  config: PropTypes.shape({
    proxyUrl: PropTypes.string,
    getHeaders: PropTypes.func,
  }).isRequired,
  children: PropTypes.node.isRequired,
}

export default function useConfig() {
  return useContext(configContext)
}
